'use client';

import React, { useState, useRef } from 'react';
import { getWesternZodiacSign, WesternZodiacResult } from '@/lib/api/client';
import {
  Sparkles, Star, Flame, Droplets, Wind, Mountain, Calendar,
  Heart, Briefcase, Gem, Flower2, Palette, Users, ArrowUp
} from 'lucide-react';

/** Element colors and icons for visual differentiation */
const ELEMENT_CONFIG: Record<string, { gradient: string; icon: React.ReactNode; badge: string }> = {
  Fire: {
    gradient: 'from-orange-500/20 via-red-500/10 to-amber-500/20',
    icon: <Flame className="w-5 h-5 text-orange-400" />,
    badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30'
  },
  Earth: {
    gradient: 'from-emerald-500/20 via-green-500/10 to-teal-500/20',
    icon: <Mountain className="w-5 h-5 text-emerald-400" />,
    badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
  },
  Air: {
    gradient: 'from-sky-500/20 via-cyan-500/10 to-blue-500/20',
    icon: <Wind className="w-5 h-5 text-sky-400" />,
    badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30'
  },
  Water: {
    gradient: 'from-indigo-500/20 via-blue-500/10 to-purple-500/20',
    icon: <Droplets className="w-5 h-5 text-indigo-400" />,
    badge: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30'
  }
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_IN_MONTH: Record<number, number> = {
  1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30,
  7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31
};

export default function WesternZodiacCalculatorPage() {
  const [month, setMonth] = useState<number>(1);
  const [day, setDay] = useState<number>(1);
  const [result, setResult] = useState<WesternZodiacResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const maxDay = DAYS_IN_MONTH[month] || 31;

  const handleMonthChange = (newMonth: number) => {
    setMonth(newMonth);
    const newMax = DAYS_IN_MONTH[newMonth] || 31;
    if (day > newMax) setDay(newMax);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await getWesternZodiacSign(month, day);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to determine zodiac sign');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateAnother = () => {
    setResult(null);
    setError(null);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const elConfig = result ? ELEMENT_CONFIG[result.element] : null;

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header Banner */}
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="flex items-center gap-2 text-[#5ab4ff] text-[11px] font-bold uppercase tracking-wider mb-2">
          <Star className="w-4 h-4 text-[#5ab4ff]" />
          <span>Western Tropical Zodiac</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-white mb-2">
          Zodiac Sign Calculator
        </h1>
        <p className="text-[rgba(220,236,255,0.85)] text-xs sm:text-sm max-w-2xl leading-[1.6]">
          Discover your Western tropical zodiac sign based on your birth date. This is the standard pop-culture zodiac used in newspapers and magazines, distinct from Vedic and sidereal astrology used elsewhere on this platform.
        </p>
      </div>

      {/* Calculator Form */}
      <div ref={formRef} className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <label htmlFor="zodiac-month" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 flex items-center">
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 text-[#5ab4ff]" />
              Birth Month
            </label>
            <select
              id="zodiac-month"
              value={month}
              onChange={(e) => handleMonthChange(parseInt(e.target.value, 10))}
              className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] focus:outline-none transition-all cursor-pointer [color-scheme:dark]"
            >
              {MONTHS.map((name, idx) => (
                <option key={idx + 1} value={idx + 1} className="bg-[#050a24] text-white">{name}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 w-full">
            <label htmlFor="zodiac-day" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
              Birth Day
            </label>
            <select
              id="zodiac-day"
              value={day}
              onChange={(e) => setDay(parseInt(e.target.value, 10))}
              className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] focus:outline-none transition-all cursor-pointer [color-scheme:dark]"
            >
              {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d} className="bg-[#050a24] text-white">{d}</option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            id="zodiac-submit-btn"
            className="w-full sm:w-auto h-12 px-8 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(150,165,255,0.4)] disabled:opacity-50 transition-all whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-[#03163d]/30 border-t-[#03163d] rounded-full animate-spin" />
                Finding...
              </span>
            ) : (
              'Find My Sign'
            )}
          </button>
        </form>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-rose-500/10 border border-[#ff9bb0]/30 rounded-xl p-4 text-[#ff9bb0] text-xs">
          {error}
        </div>
      )}

      {/* RESULT SECTIONS */}
      {result && elConfig && (
        <div className="space-y-6">
          {/* (a) Sign Header Card */}
          <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-5 sm:p-8 shadow-xl relative overflow-hidden">
            <div className="absolute top-2 right-3 sm:top-4 sm:right-6 text-6xl sm:text-[8rem] leading-none text-white/5 font-heading pointer-events-none select-none">
              {result.symbol}
            </div>
            <div className="relative z-10 flex items-center gap-3.5 sm:gap-4 min-w-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 shrink-0 rounded-xl sm:rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-2xl sm:text-3xl text-[#5ab4ff] font-heading">
                {result.symbol}
              </div>
              <div className="min-w-0 flex-1">
                <h2 id="zodiac-result-sign" className="text-xl sm:text-2xl font-heading font-semibold text-white truncate">
                  {result.sign}
                </h2>
                <p className="text-[rgba(220,236,255,0.75)] text-xs truncate">{result.dateRange}</p>
              </div>
            </div>
          </div>

          {/* (b) Quick Facts Row: Element, Quality, Ruling Planet */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
                <span className="text-[10px] font-bold text-[rgba(220,236,255,0.75)] uppercase tracking-wider">Element</span>
              </div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[#5ab4ff]/15 text-[#5ab4ff] border border-[#5ab4ff]/30">
                {result.element}
              </span>
            </div>
            <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
                <span className="text-[10px] font-bold text-[rgba(220,236,255,0.75)] uppercase tracking-wider">Quality</span>
              </div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[#b9c6ff]/15 text-[#b9c6ff] border border-[#b9c6ff]/30">
                {result.quality}
              </span>
            </div>
            <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Star className="w-4 h-4 text-[#5ab4ff]" />
                <span className="text-[10px] font-bold text-[rgba(220,236,255,0.75)] uppercase tracking-wider">Ruling Planet</span>
              </div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white border border-white/20">
                {result.rulingPlanet}
              </span>
            </div>
          </div>

          {/* (c) Birthstone / Birth Flower / Lucky Color / Lucky Day */}
          {result.birthstone && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4 text-center">
              <Gem className="w-4 h-4 text-[#5ab4ff] mx-auto mb-2" />
              <span className="text-[10px] font-bold text-[rgba(220,236,255,0.7)] uppercase tracking-wider block mb-1">Birthstone</span>
              <span className="text-xs font-semibold text-white">{result.birthstone}</span>
            </div>
            <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4 text-center">
              <Flower2 className="w-4 h-4 text-[#b9c6ff] mx-auto mb-2" />
              <span className="text-[10px] font-bold text-[rgba(220,236,255,0.7)] uppercase tracking-wider block mb-1">Birth Flower</span>
              <span className="text-xs font-semibold text-white">{result.birthFlower}</span>
            </div>
            <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4 text-center">
              <Palette className="w-4 h-4 text-[#8dffc9] mx-auto mb-2" />
              <span className="text-[10px] font-bold text-[rgba(220,236,255,0.7)] uppercase tracking-wider block mb-1">Lucky Color</span>
              <span className="text-xs font-semibold text-white">{result.luckyColor}</span>
            </div>
            <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4 text-center">
              <Calendar className="w-4 h-4 text-[#ffd36b] mx-auto mb-2" />
              <span className="text-[10px] font-bold text-[rgba(220,236,255,0.7)] uppercase tracking-wider block mb-1">Lucky Day</span>
              <span className="text-xs font-semibold text-white">{result.luckyDay}</span>
            </div>
          </div>
          )}

          {/* (d) Personality Traits: Strengths & Challenges */}
          <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 shadow-xl">
            <h3 className="text-[11px] font-bold text-[#5ab4ff] uppercase tracking-wider mb-4">
              Personality Traits
            </h3>
            <p className="text-[rgba(220,236,255,0.9)] text-xs sm:text-sm leading-[1.6] mb-5">
              {result.traits}
            </p>
            {(result.strengths || result.challenges) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Strengths */}
              {result.strengths && (
              <div>
                <h4 className="text-[10px] font-bold text-[#8dffc9] uppercase tracking-wider mb-3">Strengths</h4>
                <div className="flex flex-wrap gap-2">
                  {result.strengths.map((s) => (
                    <span key={s} className="px-3.5 py-1 rounded-full text-xs font-semibold bg-white/[0.06] text-white border border-white/15">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              )}
              {/* Challenges */}
              {result.challenges && (
              <div>
                <h4 className="text-[10px] font-bold text-[#ff9bb0] uppercase tracking-wider mb-3">Challenges</h4>
                <div className="flex flex-wrap gap-2">
                  {result.challenges.map((c) => (
                    <span key={c} className="px-3.5 py-1 rounded-full text-xs font-semibold bg-rose-500/15 text-[#ff9bb0] border border-[#ff9bb0]/30">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              )}
            </div>
            )}
          </div>

          {/* (e) Love & Relationships + Career & Success */}
          {(result.loveAndRelationships || result.careerAndSuccess) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Love & Relationships */}
            {result.loveAndRelationships && (
            <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 shadow-xl">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-4 h-4 text-[#ff9bb0]" />
                <h3 className="text-[11px] font-bold text-white uppercase tracking-wider">Love &amp; Relationships</h3>
              </div>
              <p className="text-[rgba(220,236,255,0.85)] text-xs sm:text-sm leading-[1.6] mb-4">
                {result.loveAndRelationships}
              </p>
              {result.bestCompatibility && (
              <div>
                <span className="text-[10px] font-bold text-[rgba(220,236,255,0.7)] uppercase tracking-wider block mb-2">Best Compatibility</span>
                <div className="flex flex-wrap gap-2">
                  {result.bestCompatibility.map((sign) => (
                    <span key={sign} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#b9c6ff]/15 text-[#b9c6ff] border border-[#b9c6ff]/30">
                      {sign}
                    </span>
                  ))}
                </div>
              </div>
              )}
            </div>
            )}
            {/* Career & Success */}
            {result.careerAndSuccess && (
            <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 shadow-xl">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="w-4 h-4 text-[#5ab4ff]" />
                <h3 className="text-[11px] font-bold text-white uppercase tracking-wider">Career &amp; Success</h3>
              </div>
              <p className="text-[rgba(220,236,255,0.85)] text-xs sm:text-sm leading-[1.6]">
                {result.careerAndSuccess}
              </p>
            </div>
            )}
          </div>
          )}

          {/* (f) Lucky Elements Summary Strip */}
          {(result.luckyDay || result.luckyNumbers) && (
          <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-5 shadow-xl">
            <h3 className="text-[11px] font-bold text-white uppercase tracking-wider mb-4">Lucky Elements</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <span className="text-xs text-[rgba(220,236,255,0.7)] block mb-1">Lucky Day</span>
                <span className="text-xs font-semibold text-[#ffd36b]">{result.luckyDay}</span>
              </div>
              <div>
                <span className="text-xs text-[rgba(220,236,255,0.7)] block mb-1">Lucky Numbers</span>
                <div className="flex flex-wrap gap-1.5">
                  {result.luckyNumbers?.map((n) => (
                    <span key={n} className="w-7 h-7 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-xs font-bold font-mono text-white">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs text-[rgba(220,236,255,0.7)] block mb-1">Element</span>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#5ab4ff]/15 text-[#5ab4ff] border border-[#5ab4ff]/30">
                  {result.element}
                </span>
              </div>
              <div>
                <span className="text-xs text-[rgba(220,236,255,0.7)] block mb-1">Quality</span>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#b9c6ff]/15 text-[#b9c6ff] border border-[#b9c6ff]/30">
                  {result.quality}
                </span>
              </div>
            </div>
          </div>
          )}

          {/* (g) Famous Personalities */}
          {result.famousPersonalities && (
          <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-[#5ab4ff]" />
              <h3 className="text-[11px] font-bold text-white uppercase tracking-wider">Famous Personalities</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {result.famousPersonalities.map((name) => (
                <span key={name} className="px-4 py-1.5 rounded-full text-xs font-semibold bg-white/[0.06] text-white border border-white/15">
                  {name}
                </span>
              ))}
            </div>
          </div>
          )}

          {/* (h) Calculate for Another Date button */}
          <div className="flex justify-center">
            <button
              onClick={handleCalculateAnother}
              id="zodiac-recalculate-btn"
              className="px-8 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white border border-white/20 backdrop-blur-md transition-all flex items-center gap-2 cursor-pointer font-semibold text-xs"
            >
              <ArrowUp className="w-4 h-4" />
              Calculate for Another Date
            </button>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-[rgba(220,236,255,0.75)] italic text-center">
            This uses the Western tropical zodiac based on fixed calendar date ranges. For Vedic
            (sidereal) calculations using your precise birth time and location, see our{' '}
            <a href="/" className="text-[#5ab4ff] hover:underline font-semibold">
              Birth Chart Calculator
            </a>.
          </p>
        </div>
      )}
    </div>
  );
}
